/* istanbul ignore file */
import { Dispatch, ReactElement, SetStateAction } from 'react'

import { Vector2 } from 'three'

import { PointArray } from './attribute'
import { CameraProfile, FrontCameraProfile } from './camera'
import { EditorDisplayConfig } from './editorSettings'
import { ErrorResponse } from './errors'
import { File, FileProcessingStatus } from './file'
import { InspectionGroupable } from './inspectionGroup'
import { InspectionItemGrid } from './inspectionItemGrid'
import { ShapeKeyType } from './shape'

/**
 * Pre-defined threshold keys. This must be in sync with the API.
 */
export type InspectionItemPreDefinedThresholdsKey =
  | 'designed_value'
  | 'tolerance'
  | 'upper_tolerance'
  | 'lower_tolerance'

/**
 * Pre-defined threshold values
 */
export type InspectionItemPredefinedThresholds = { [name in InspectionItemPreDefinedThresholdsKey]?: number | null } // null only for FE, don't send null to API
export interface InspectionItemUserDefinedThresholds {
  /**
   * Unique key for the threshold
   */
  key?: string

  /**
   * Name of the threshold
   */
  name: string

  /**
   * Value of the threshold
   */
  value: string
}

export interface ValuesEditModalConfig {
  /**
   * Title of the modal
   */
  title: string

  /**
   * Unit of the value
   */
  unit: string | ReactElement

  /**
   * Current value to be edited
   */
  value?: number

  /**
   * Type of the value
   */
  valueType?: 'text' | 'number'

  /**
   * Pre-defined threshold values
   */
  preDefinedThreshold?: InspectionItemPredefinedThresholds

  /**
   * User-defined threshold values
   */
  userDefinedThreshold?: InspectionItemUserDefinedThresholds[]

  /**
   * Inspection item id
   */
  inspectionSheetItemId?: string

  /**
   * Provide the name to be edited, if it were to be editable.
   * Skip this if the name is not editable.
   */
  name?: string
}

export interface InspectionSheetDiagram {
  image: string
  width: number
  height: number
}

export interface MainSheet {
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void

  editingField: string
  setEditingField: (field: string) => void

  editingValue: string | Date
  setEditingValue: (value: string | Date) => void

  selectedItemIndex: number
  setSelectedItemIndex: (selectedItemIndex: number) => void

  itemResults: InspectionEvaluateResult[]
  setItemResults: (itemResults: InspectionEvaluateResult[]) => void

  setValuesEditingConfig: (value: ValuesEditModalConfig) => void

  loadingItems: { [itemId: string]: boolean }
  setLoadingItems: Dispatch<SetStateAction<{ [itemId: string]: boolean }>>

  updateItem: (updatedVolumeItem: InspectionItem) => Promise<void>
}

export interface PCDAlignment {
  transformation: number[]
  base_area_id: string
  target_area_id: string
  base_anchor_positions: number[][]
  target_anchor_positions: number[][]
}

export interface InspectionArea {
  /**
   * Unique identifier for the inspection area
   */
  inspection_area_id: string

  /**
   * Name of the inspection area
   */
  inspection_area_name: string

  /**
   * Original file uploaded by the user
   */
  origin_file?: File

  /**
   * Downsampled file for preview
   */
  down_sampled_file?: File & {
    /**
     * Directory for potree files, if applicable
     */
    potree_dir?: string
  }

  /**
   * Solid mesh uploaded by the user
   */
  mesh_bundle_file?: Omit<File, 'size'>

  /**
   * Downsample status of the inspection area
   */
  downsample_status?: {
    /**
     * Status of the downsample processing
     */
    status: FileProcessingStatus

    /**
     * Unique identifier for the status. Will contain error code if status is FAILED.
     */
    status_id: number

    /**
     * Message for the status. Will contain error message if status is FAILED.
     */
    status_message: string
  }

  /**
   * Status of the mesh file processing
   */
  mesh_bundle_status?: {
    /**
     * Status of the mesh processing
     */
    status: FileProcessingStatus

    /**
     * Unique identifier for the status. Will contain error code if status is FAILED.
     */
    status_id: number

    /**
     * Message for the status. Will contain error message if status is FAILED.
     */
    status_message: string

    /**
     * Timestamp indicating when the mesh file was uploaded.
     */
    uploaded_at: number
  }

  /**
   * Translation of the PCD due to re-centering.
   */
  translation: [number, number, number]

  /**
   * Generated CAD file for the inspection area
   */
  cad_file?: File

  /**
   * Generated 2D CAD file for the inspection area
   */
  cad_file_2d?: File

  /**
   * Number of images uploaded within the inspection area
   */
  image_number?: number

  /**
   * Number of blueprints uploaded within the inspection area
   */
  blueprint_number?: number

  /**
   * Project the inspection area belongs to
   */
  project_id: string

  /**
   * Unique identifier for the user who created the inspection area
   */
  user_id: string

  /**
   * Date and time the inspection area was created
   */
  created_at: number

  /**
   * Date and time the inspection area was last updated
   */
  updated_at: number

  /**
   * Date and time the inspection area was deleted
   */
  deleted_at?: number | null

  /**
   * Camera profile associated with the inspection area, if any.
   */
  camera_profile?: CameraProfile

  /**
   * User-specified order of the inspection area
   */
  order?: number

  /**
   * A record of PCD alignments with area IDs as keys.
   */
  pcd_alignments?: Record<string, PCDAlignment>

  /**
   * CAD file for blueprint generation, if any.
   */
  cad_file_blueprint?: File

  /**
   * Editor display configuration
   */
  display_config?: EditorDisplayConfig
}

export type InspectionShapeIds = Record<ShapeKeyType, string[]>

/**
 * App-only states for InspectionItem
 */
export interface InspectionItemAppStates {
  // Indicates if the part name has been defaulted by the system
  isPartNameDefaulted?: boolean
}

export interface InspectionItemRequestData {
  part_name: string
  need_update?: boolean
  pre_defined_thresholds?: InspectionItemPredefinedThresholds
  user_defined_thresholds?: InspectionItemUserDefinedThresholds[]
  is_shown_on_final_sheet?: boolean
}

export enum InspectionItemType {
  Volume = 'volume',
  PlaneToPlaneDistance = 'plane_to_plane_distance',
  PlaneToCylindersDistance = 'plane_to_cylinders_distance',
  Grid = 'grid',
  PolygonLength1 = 'polygon_length_1',
  PolygonLength2 = 'polygon_length_2',
  PolygonPerimeter = 'polygon_perimeter',
  PolygonArea = 'polygon_area',
  LengthWithDistanceTool = 'length_with_distance_tool',
  EdgeLength = 'edge_length',
  PolylineLength = 'polyline_length',
  PolylineWidth = 'polyline_width',
}

export interface InspectionItem
  extends ErrorResponse, InspectionItemRequestData, InspectionGroupable, InspectionItemAppStates {
  partition_key?: string
  cover_distance?: InspectionItemNumberValues
  diameter?: InspectionItemNumberValues
  inspection_item_id?: string
  mean_distance?: InspectionItemNumberValues
  number?: InspectionItemNumberValues
  volume?: InspectionItemVolume
  plane_to_cylinders_distance?: InspectionItemNumberValues
  /**
   * @deprecated
   */
  plane_to_plane_distance?: InspectionItemNumberValues
  grid?: InspectionItemGrid
  length_with_distance_tool?: InspectionItemNumberValues
  situation?: string
  shape_ids: InspectionShapeIds
  item_type?: InspectionItemType

  polyline_length?: InspectionItemNumberValues
  polyline_width?: InspectionItemNumberValues
  polyline_length_id?: string

  volume_id?: string
  plane_side?: 'upper' | 'lower'
  polygon_area?: InspectionItemNumberValues
  polygon_perimeter?: InspectionItemNumberValues
  polygon_length_1?: InspectionItemNumberValues
  polygon_length_2?: InspectionItemNumberValues
  edge_length?: InspectionItemNumberValues

  camera_profile?: FrontCameraProfile | null

  inspection_area_id?: string
  inspection_sheet_id?: string
}

export interface InspectionItemIndividualDistance {
  distance: number
  positions_for_distance: PointArray[]
}

export interface InspectionItemNumberValues {
  estimated_value?: number | null
  specified_value?: number | null
  positions_for_distance?: PointArray[]
  individual_distances?: InspectionItemIndividualDistance[]
  max_individual_distances?: InspectionItemIndividualDistance[]
  index?: number
}

export interface InspectionItemVolume extends InspectionItemNumberValues {
  is_flat_plane: boolean
  is_valid_method?: boolean
  nan_depth_ratio?: number
}

export interface InspectionSheet {
  partition_key?: string
  inspection_area_id?: string
  inspection_sheet_id: string
  construction_properties: {
    construction_project_name?: string
    construction_type?: string
    construction_type_detailed?: string
  }
  creator_name: string
  create_time_user_specified: string | null
  observer_name: string
  observe_time_user_specified: string | null
}

export interface InspectionEvaluateResult {
  contractee: boolean
  contractor: boolean
}

export interface PlaneInspectionItems {
  area: InspectionItem | undefined
  perimeter: InspectionItem | undefined
  length1: InspectionItem | undefined
  length2: InspectionItem | undefined
}

export interface PolygonDiagramVertice {
  label: {
    text: string
    point: Vector2
  }
  point: Vector2
}

export enum VolumeEstimationMethods {
  Standard = '標準',
  Advanced = '高度',
}
